import { FC, useEffect } from 'react';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { useDispatch, useSelector } from 'react-redux';
import { activePageSelector } from '../../../../redux/pages/selectors';
import { Select } from '../../../../types/redux/pages/PagesStore';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
} from '../../../../redux/pages/actions';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import LiquiditModelBreakdownTable from './subComponents/LiquiditModelBreakdownTable.component';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useLiquidityModelBreakdownPageModel } from '../../../../api/rs-hg/liquidity/hooks/queries';

// Configuration for the 'Volume or Days' dropdown in the title bar.
const VOLUME_OR_DAYS_SELECT: Select = {
  titleBarTitle: 'Volume or Days:',
  titleBarKey: 'volumeOrDays',
  values: [
    { label: 'Volume', value: 'volume' },
    { label: 'Days to Liquidate', value: 'days' },
  ],
  displayOnOverviewPage: false,
  aValueIsAlwaysSelected: true,
  currentValue: 'volume',
};

// Configuration for the 'Participation Rate' dropdown in the title bar.
const PARTICIPATION_RATE_SELECT: Select = {
  titleBarTitle: 'Participation Rate:',
  titleBarKey: 'participationRate',
  values: [
    { label: '5%', value: '5' },
    { label: '10%', value: '10' },
    { label: '20%', value: '20' },
    { label: '30%', value: '30' },
    { label: '40%', value: '40' },
    { label: '50%', value: '50' },
    { label: '60%', value: '60' },
    { label: '70%', value: '70' },
    { label: '80%', value: '80' },
    { label: '90%', value: '90' },
    { label: '100%', value: '100' },
  ],
  displayOnOverviewPage: false,
  aValueIsAlwaysSelected: true,
  currentValue: '10',
};

const LiquidityModelBreakdown: FC<FundInfoComponentProps> = ({
  fundId,
  positionDate,
}) => {
  const dispatch = useDispatch();
  const activePage = useSelector(activePageSelector);
  const volumeOrDays = useTitleBarSelect('volumeOrDays');
  const participationRate = useTitleBarSelect('participationRate');

  // When 'Days to Liquidate' is selected, the participation rate dropdown appears.
  // If no rate has been selected yet (e.g., right after switching from 'Volume'),
  // we default to '10' to ensure a valid API call and prevent errors from a null value.
  const effectiveParticipationRate = participationRate || '10';
  const isDaysView = volumeOrDays === 'days';

  // This effect manages the presence of the main 'Volume or Days' dropdown in the title bar.
  // It ensures the component is added on mount and cleaned up on unmount to prevent it
  // from appearing on other pages.
  useEffect(() => {
    dispatch(
      addAdditionalTitleBarComponents(
        [VOLUME_OR_DAYS_SELECT],
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );

    return () => {
      dispatch(
        removeTitleBarComponent('volumeOrDays', REMOVE_SELECT_FROM_TITLEBAR),
      );
      dispatch(
        removeTitleBarComponent(
          'participationRate',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
    };
  }, [activePage, dispatch]);

  // This effect conditionally shows or hides the 'Participation Rate' dropdown.
  // It is only displayed when the user selects 'Days to Liquidate'.
  useEffect(() => {
    if (isDaysView) {
      dispatch(
        addAdditionalTitleBarComponents(
          [PARTICIPATION_RATE_SELECT],
          APPEND_NEW_SELECT_TO_TITLEBAR,
        ),
      );
    } else {
      dispatch(
        removeTitleBarComponent(
          'participationRate',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
    }
  }, [isDaysView, dispatch]);

  // Build API endpoint and cache key for liquidity breakdown, including participation rate if in 'days' view
  const query = useLiquidityModelBreakdownPageModel({
    fundId,
    positionDate,
    participationRate: isDaysView ? effectiveParticipationRate : undefined,
  });

  return (
    <QueryBoundary
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      isError={query.isError}
      error={query.error}
      data={query.data?.data}
      loadingMessages={['Loading Liquidity Data...']}
      noDataMessage="No Data Available."
    >
      {(pageData) => (
        <LiquiditModelBreakdownTable
          pageData={pageData}
          volumeOrDays={volumeOrDays as 'volume' | 'days'}
          participationRate={effectiveParticipationRate}
        />
      )}
    </QueryBoundary>
  );
};

export default LiquidityModelBreakdown;
